<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BPagination,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { useQuery } from '@vue/apollo-composable';
import { GetInvitesDocument, InviteType } from '@/gql/graphql';
import { formatDate } from '@/utils/date-utils';
import { truncateText } from '@/utils/string-utils';
import { deepParseDates } from '@/utils/date-utils';
import { PAGING_ITEMS_PER_PAGE, TRUNCATE_TEXT_LENGTH } from '@/constants';
import { useUserStore } from '@/store/user';
import InviteDetail from '@/components/invite/InviteDetail.vue';
import InviteAccept from '@/components/invite/InviteAccept.vue';
import InviteCancel from '@/components/invite/InviteCancel.vue';
import InviteResend from '@/components/invite/InviteResend.vue';
import InviteRevoke from '@/components/invite/InviteRevoke.vue';

const userStore = useUserStore();

// --- Props for the component ---
interface Props {
  invitedUserId?: number | null;
  inviterUserId?: number | null;
  organizationId?: number | null;
  userRole?: string | null;
  statusPattern?: string | null;
  emailPattern?: string | null;
  showStatus?: boolean;
  adminView?: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['invitesUpdated', 'countUpdated']);

const invitedView = computed<boolean>(() => {
  if (props.adminView) {
    return false;
  }
  return !!props.invitedUserId || props.emailPattern === userStore.email;
});

const inviterView = computed<boolean>(() => {
  if (props.adminView) {
    return false;
  }
  return !!props.inviterUserId;
});

// --- Query invites ---
const pagingPageCurrent = ref<number>(1);
const pagingItemsPerPage = ref<number>(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return invitesResult.value?.invites?.total || 0;
});

const invites = computed<InviteType[]>(() => {
  const rawList = invitesResult.value?.invites?.invites || [];
  console.debug('Raw invites:', rawList);
  const parsedList = rawList.map((inv) => ({
    ...(deepParseDates(inv) as InviteType),
  }));
  console.debug('Invites:', parsedList);
  return parsedList;
});

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  refetchInvites();
});

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1; // this will trigger the watcher -> requestRefetchTasks
  } else {
    refetchInvites();
  }
}

const {
  result: invitesResult,
  loading: loadingInvitesQuery,
  error: errorInvitesQuery,
  refetch: refetchInvites,
} = useQuery(
  GetInvitesDocument,
  {
    userIdInvited: props.invitedUserId,
    userIdInviter: props.inviterUserId,
    organizationId: props.organizationId,
    userRole: props.userRole,
    emailPattern: props.emailPattern,
    statusPattern: props.statusPattern,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  },
  {
    fetchPolicy: 'no-cache',
  }
);

watch(
  pagingTotalRows,
  (newCount) => {
    console.debug('Emitting countUpdated:', newCount);
    emits('countUpdated', newCount);
  },
  { immediate: true }
);

// Refs to handle the modal & the ID of the invites to be acted upon
const showDetailModal = ref(false);
const showAcceptModal = ref(false);
const showCancelModal = ref(false);
const showResendModal = ref(false);
const showRevokeModal = ref(false);
const selectedInvite = ref<InviteType | null>(null);

// --- Invite table fields (dynamic)  ---
const inviteTableFields = computed(() => {
  const fields = [
    {
      key: 'invitedOrganizationUserRole',
      label: 'Role',
      sortable: false,
      tdClass: 'align-middle',
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      tdClass: 'align-middle',
    },
    {
      key: 'expiresAt',
      label: 'Expires At',
      sortable: false,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    { key: 'id', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  // show status if requested
  if (props.showStatus) {
    fields.splice(3, 0, {
      key: 'status',
      label: 'Status',
      sortable: false,
      tdClass: 'align-middle',
    });
  }

  // admin/invited -> show inviter and org
  if (props.adminView || invitedView.value) {
    fields.splice(0, 0, {
      key: 'inviter',
      label: 'Invited By',
      sortable: false,
      tdClass: 'align-middle',
    });
    fields.splice(0, 0, {
      key: 'organizationName',
      label: 'Organization',
      sortable: false,
      tdClass: 'align-middle',
    });
  }

  // admin/inviter -> show recipient
  if (props.adminView || inviterView.value) {
    fields.splice(0, 0, {
      key: 'invited',
      label: 'Recipient',
      sortable: false,
      tdClass: 'align-middle',
    });
  }

  return fields;
});

// --- Handle mutation requests ---
function handleInvitesUpdated() {
  refetchInvites();
  emits('invitesUpdated');
}

// --- Expose methods to parent component ---
defineExpose({
  requestSearch,
  pagingTotalRows,
  loadingInvitesQuery,
  errorInvitesQuery,
});

// --- Handlers ---
function acceptButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to accept');
    return;
  }
  selectedInvite.value = invite;
  showAcceptModal.value = true;
}

function cancelButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to cancel');
    return;
  }
  selectedInvite.value = invite;
  showCancelModal.value = true;
}

function resendButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to resend');
    return;
  }
  selectedInvite.value = invite;
  showResendModal.value = true;
}

function revokeButtonClicked(invite: InviteType) {
  if (!invite) {
    console.error('No invite to revoke');
    return;
  }
  selectedInvite.value = invite;
  showRevokeModal.value = true;
}

function inviteRowClicked(invite: InviteType, index: number, event: Event) {
  if (!invite) {
    console.error('No invite to show details for');
    return;
  }
  console.debug('Invite row clicked:', invite);
  selectedInvite.value = invite;
  showDetailModal.value = true;
}

function handleModalsClosed() {
  // clear the refs
  selectedInvite.value = null;

  // close the modals
  showDetailModal.value = false;
  showAcceptModal.value = false;
  showCancelModal.value = false;
  showResendModal.value = false;
  showRevokeModal.value = false;
}
</script>

<template>
  <div>
    <BCard v-if="invites.length > 0">
      <!-- Paging (above) -->
      <BPagination
        v-if="pagingItemsPerPage > 0 && pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
        class="mb-2"
      />

      <!-- Invites table -->
      <b-table
        :items="invites"
        :busy="loadingInvitesQuery"
        :fields="inviteTableFields"
        show-empty
        small
        striped
        responsive
        hover
        class="browser-table"
        @row-clicked="inviteRowClicked"
      >
        <!-- table busy -->
        <template #table-busy>
          <div
            class="d-flex align-items-center justify-content-center"
            style="min-height: 100px"
          >
            <b-spinner small class="align-middle me-1"></b-spinner>
            <span class="ms-1">Loading invites...</span>
          </div>
        </template>

        <!-- table empty -->
        <template #empty>
          <div class="text-center my-5">
            <p class="text-muted">No invites found.</p>
          </div>
        </template>

        <!-- table content -->
        <template #cell(organizationName)="data">
          {{ data.item.organization?.name }}
        </template>
        <template #cell(invited)="data">
          <template v-if="data.item.invited">
            <span
              >{{ data.item.invited.givenName }}
              {{ data.item.invited.familyName }}</span
            >
            <font-awesome-icon
              :icon="['fas', 'envelope']"
              class="ms-2 text-muted"
              :id="'icon-invited-email-' + data.item.id"
            />
            <BTooltip
              :target="'icon-invited-email-' + data.item.id"
              triggers="hover"
            >
              {{ data.item.invitedEmail }}
            </BTooltip>
          </template>
          <template v-else>
            {{ data.item.invitedEmail }}
          </template>
        </template>
        <template #cell(inviter)="data">
          <span>
            {{ data.item.inviter.givenName }}
            {{ data.item.inviter.familyName }}
          </span>
          <font-awesome-icon
            :icon="['fas', 'envelope']"
            class="ms-2 text-muted"
            :id="`icon-inviter-email-` + data.item.id"
          />
          <BTooltip
            :target="`icon-inviter-email-` + data.item.id"
            triggers="hover"
          >
            {{ data.item.inviter.email }}
          </BTooltip>
        </template>
        <template #cell(description)="data">
          {{ truncateText(data.item.description, TRUNCATE_TEXT_LENGTH) }}
        </template>

        <template #cell(id)="data">
          <BButtonGroup>
            <b-button
              v-if="adminView || invitedView"
              variant="outline-primary"
              size="sm"
              @click.prevent="acceptButtonClicked(data.item)"
            >
              <font-awesome-icon :icon="['fas', 'check']" /> Accept
            </b-button>
            <b-button
              v-if="invitedView"
              variant="outline-danger"
              size="sm"
              @click.prevent="revokeButtonClicked(data.item)"
            >
              <font-awesome-icon :icon="['fas', 'times']" /> Revoke
            </b-button>
            <b-button
              v-if="adminView || inviterView"
              variant="outline-secondary"
              size="sm"
              @click.prevent="resendButtonClicked(data.item)"
            >
              <font-awesome-icon :icon="['fas', 'envelope']" /> Resend
            </b-button>
            <b-button
              v-if="adminView || inviterView"
              variant="outline-danger"
              size="sm"
              @click.prevent="cancelButtonClicked(data.item)"
            >
              <font-awesome-icon :icon="['fas', 'times']" /> Cancel
            </b-button>
          </BButtonGroup>
        </template>
      </b-table>

      <!-- Paging (below) -->
      <BPagination
        v-if="pagingItemsPerPage > 0 && pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
        class="mb-2"
      />

      <!-- Invite Detail Modal -->
      <InviteDetail
        v-if="selectedInvite"
        :invite="selectedInvite"
        :show="showDetailModal"
        @close="handleModalsClosed"
      />

      <!-- Invite Accept Modal -->
      <InviteAccept
        v-if="selectedInvite"
        :invite="selectedInvite"
        :show="showAcceptModal"
        :adminAccess="adminView"
        @close="handleModalsClosed"
        @success="handleInvitesUpdated"
      />

      <!-- Invite Cancel Modal -->
      <InviteCancel
        v-if="selectedInvite"
        :invite="selectedInvite"
        :show="showCancelModal"
        @close="handleModalsClosed"
        @success="handleInvitesUpdated"
      />

      <!-- Invite Resend Modal -->
      <InviteResend
        v-if="selectedInvite"
        :invite="selectedInvite"
        :show="showResendModal"
        @close="handleModalsClosed"
        @success="handleInvitesUpdated"
      />

      <!-- Invite Revoke Modal -->
      <InviteRevoke
        v-if="selectedInvite"
        :invite="selectedInvite"
        :show="showRevokeModal"
        @close="handleModalsClosed"
        @success="handleInvitesUpdated"
      />
    </BCard>

    <!-- Query Error -->
    <BAlert
      v-model="errorInvitesQuery"
      variant="danger"
      dismissible
      class="mt-3"
    >
      {{ errorInvitesQuery }}
      <BButton size="sm" variant="outline-danger" @click="refetchInvites">
        Retry
        <font-awesome-icon :icon="['fas', 'sync']" class="ms-1" />
      </BButton>
    </BAlert>
  </div>
</template>
